/**
 * BLOCK 187 — first-touch marketing attribution.
 *
 * Captured once per visitor (first touch wins, never overwritten) from the
 * query string + document.referrer on app boot, stored client-side in
 * localStorage with a 30-day expiry. Read back at signup time and attached
 * to the register / seller-create-account request body.
 *
 * Never stores IP, email, or any personal data — only the marketing
 * campaign params, the referrer URL, the landing path, and a timestamp.
 *
 * Usage:
 *   import { captureAttribution, readAttribution } from "@/lib/attribution";
 *   useEffect(() => { captureAttribution(); }, []);          // app boot
 *   const attribution = readAttribution();                    // at signup
 */
const STORAGE_KEY = "ae_attribution";
const EXPIRY_DAYS = 30;
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content"];
const MAX_LEN = 200;

function readRecord() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    if (!record || !record.expires_at || Date.now() > record.expires_at) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return record;
  } catch {
    return null;
  }
}

/**
 * Capture utm_source/medium/campaign/content + referrer + landing path on
 * first page load. First touch wins — a no-op if attribution was already
 * captured for this visitor (even if the current visit has no UTM params).
 * Idempotent and safe to call on every page/route mount.
 */
export function captureAttribution() {
  if (readRecord()) return; // already captured — first touch wins
  try {
    const params = new URLSearchParams(window.location.search);
    const attribution = {};
    UTM_PARAMS.forEach((key) => {
      const val = params.get(key);
      if (val) attribution[key] = val.slice(0, MAX_LEN);
    });
    if (document.referrer) attribution.referrer = document.referrer.slice(0, MAX_LEN);
    attribution.landing_path = window.location.pathname.slice(0, MAX_LEN);
    attribution.captured_at = new Date().toISOString();

    const record = { value: attribution, expires_at: Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000 };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage unavailable (private mode, quota, etc.) — never block the app.
  }
}

/** Read back the captured attribution object, or null if none/expired. */
export function readAttribution() {
  const record = readRecord();
  return record ? record.value : null;
}
